import { Children, isValidElement, useState } from 'react'
import type { ReactElement, ReactNode } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { Check } from '@nine-thirty-five/material-symbols-react/sharp'
import { Dialog as BaseDialog } from '@base-ui/react/dialog'
import { Select as BaseSelect } from '@base-ui/react/select'
import { Menu as BaseMenu } from '@base-ui/react/menu'
import { Slider as BaseSlider } from '@base-ui/react/slider'
import { Tooltip as BaseTooltip } from '@base-ui/react/tooltip'
import { Radio as BaseRadio } from '@base-ui/react/radio'
import { Checkbox as BaseCheckbox } from '@base-ui/react/checkbox'
import { Field as BaseField } from '@base-ui/react/field'
import { RadioGroup as BaseRadioGroup } from '@base-ui/react/radio-group'
import { Tabs as BaseTabs } from '@base-ui/react/tabs'

/*
 * The interactive components, on Base UI primitives. These are the parts MUI
 * was genuinely earning its weight for -- focus trapping, dismissal, typeahead,
 * collision-aware positioning and the ARIA wiring -- so they are thin wrappers
 * rather than reimplementations, styled by ui.css.
 */

/* ------------------------------------------------------------------ dialog */

export interface DialogProps {
  open: boolean
  onClose?: () => void
  title?: ReactNode
  children?: ReactNode
  actions?: ReactNode
  className?: string
}

export function Dialog({ open, onClose, title, children, actions, className = '' }: DialogProps) {
  return (
    <BaseDialog.Root open={open} onOpenChange={next => !next && onClose?.()}>
      <BaseDialog.Portal>
        <BaseDialog.Backdrop className="ui-backdrop" />
        <BaseDialog.Popup className={`ui-dialog ${className}`}>
          {title ? <BaseDialog.Title className="ui-dialog__title">{title}</BaseDialog.Title> : null}
          <div className="ui-dialog__body">{children}</div>
          {actions ? <div className="ui-dialog__actions">{actions}</div> : null}
        </BaseDialog.Popup>
      </BaseDialog.Portal>
    </BaseDialog.Root>
  )
}

export const DialogContent = ({ children }: { children?: ReactNode }) => (
  <div className="ui-dialog__body">{children}</div>
)

export const DialogActions = ({ children }: { children?: ReactNode }) => (
  <div className="ui-dialog__actions">{children}</div>
)

export const DialogTitle = ({ children }: { children?: ReactNode }) => (
  <div className="ui-dialog__title">{children}</div>
)

export const DialogContentText = ({ children }: { children?: ReactNode }) => <p>{children}</p>

/* ------------------------------------------------------------------ select */

export interface SelectOption<T> {
  value: T
  label: ReactNode
}

export interface SelectProps<T> {
  value?: T
  onChange: (value: T) => void
  options?: SelectOption<T>[]
  children?: ReactNode
  label?: ReactNode
  id?: string
  className?: string
}

export function Select<T extends string | number>({
  value,
  onChange,
  options,
  children,
  label,
  id,
  className = ''
}: SelectProps<T>) {
  /*
   * Call sites arrived in two shapes: an `options` array, and MUI's
   * `<Select><MenuItem value=...>` children. Children are normalised into the
   * same option list so there is only one rendering path.
   */
  const resolved: SelectOption<T>[] =
    options ??
    Children.toArray(children)
      .filter(isValidElement)
      .map(child => {
        const props = (child as ReactElement<{ value: T; children: ReactNode }>).props
        return { value: props.value, label: props.children }
      })

  /*
   * Some call sites are uncontrolled (no `value` prop) and expect the trigger
   * to display whatever was last picked, the way MUI's Select did. Track that
   * pick locally so the controlled and uncontrolled paths render the same.
   */
  const [internal, setInternal] = useState<T | undefined>(undefined)
  const effective = value !== undefined ? value : internal
  const selected = resolved.find(option => option.value === effective)

  return (
    <BaseField.Root className={`ui-field ${className}`}>
      {label ? <BaseField.Label className="ui-field__label">{label}</BaseField.Label> : null}
      <BaseSelect.Root
        value={effective ?? null}
        onValueChange={next => {
          setInternal(next as T)
          onChange(next as T)
        }}
      >
        <BaseSelect.Trigger id={id} className="ui-select__trigger">
          <BaseSelect.Value>{selected?.label ?? ''}</BaseSelect.Value>
          <BaseSelect.Icon>▾</BaseSelect.Icon>
        </BaseSelect.Trigger>
        <BaseSelect.Portal>
          <BaseSelect.Positioner className="ui-positioner" sideOffset={4} alignItemWithTrigger={false}>
            <BaseSelect.Popup className="ui-popup">
              {resolved.map(option => (
                <BaseSelect.Item key={String(option.value)} value={option.value} className="ui-option">
                  <BaseSelect.ItemText>{option.label}</BaseSelect.ItemText>
                  <BaseSelect.ItemIndicator className="ui-option__check">
                    <Check size={16} />
                  </BaseSelect.ItemIndicator>
                </BaseSelect.Item>
              ))}
            </BaseSelect.Popup>
          </BaseSelect.Positioner>
        </BaseSelect.Portal>
      </BaseSelect.Root>
    </BaseField.Root>
  )
}

/* -------------------------------------------------------------------- menu */

export interface MenuProps {
  trigger: ReactNode
  children: ReactNode
}

export function Menu({ trigger, children }: MenuProps) {
  return (
    <BaseMenu.Root>
      <BaseMenu.Trigger render={trigger as any} />
      <BaseMenu.Portal>
        <BaseMenu.Positioner className="ui-positioner" sideOffset={4} align="end">
          <BaseMenu.Popup className="ui-popup">{children}</BaseMenu.Popup>
        </BaseMenu.Positioner>
      </BaseMenu.Portal>
    </BaseMenu.Root>
  )
}

export interface MenuItemProps {
  onClick?: () => void
  to?: string
  href?: string
  target?: string
  rel?: string
  value?: string | number
  sx?: Record<string, unknown>
  children: ReactNode
}

export function MenuItem({ onClick, to, href, target, rel, children }: MenuItemProps) {
  if (to) {
    return (
      <BaseMenu.Item
        className="ui-option"
        onClick={onClick}
        render={<RouterLink to={to}>{children}</RouterLink>}
      />
    )
  }

  if (href) {
    return (
      <BaseMenu.Item
        className="ui-option"
        onClick={onClick}
        render={
          <a href={href} target={target} rel={rel}>
            {children}
          </a>
        }
      />
    )
  }

  return (
    <BaseMenu.Item className="ui-option" onClick={onClick}>
      {children}
    </BaseMenu.Item>
  )
}

export const MenuSeparator = () => <BaseMenu.Separator className="ui-separator" />

/* ------------------------------------------------------------------ slider */

export interface SliderProps {
  value: number
  onChange: (value: number, event?: unknown) => void
  onCommit?: (value: number) => void
  min?: number
  max?: number
  step?: number
  size?: 'small' | 'medium'
  className?: string
  'aria-label'?: string
}

export function Slider({
  value,
  onChange,
  onCommit,
  min = 0,
  max = 100,
  step = 1,
  size: _size,
  className = '',
  ...props
}: SliderProps) {
  return (
    <BaseSlider.Root
      value={value}
      min={min}
      max={max}
      step={step}
      onValueChange={next => onChange(Array.isArray(next) ? next[0] : next)}
      onValueCommitted={next => onCommit?.(Array.isArray(next) ? next[0] : next)}
      className={className}
    >
      <BaseSlider.Control className="ui-slider">
        <BaseSlider.Track className="ui-slider__track">
          <BaseSlider.Indicator className="ui-slider__fill" />
          <BaseSlider.Thumb className="ui-slider__thumb" {...props} />
        </BaseSlider.Track>
      </BaseSlider.Control>
    </BaseSlider.Root>
  )
}

/* ----------------------------------------------------------------- tooltip */

export function Tooltip({
  title,
  placement = 'top',
  children
}: {
  title: ReactNode
  placement?: string
  children: ReactNode
}) {
  if (!title) return <>{children}</>

  return (
    <BaseTooltip.Root>
      <BaseTooltip.Trigger render={children as any} />
      <BaseTooltip.Portal>
        <BaseTooltip.Positioner className="ui-positioner" sideOffset={6} side={placement as any}>
          <BaseTooltip.Popup className="ui-tooltip">{title}</BaseTooltip.Popup>
        </BaseTooltip.Positioner>
      </BaseTooltip.Portal>
    </BaseTooltip.Root>
  )
}

export const TooltipProvider = BaseTooltip.Provider

/* ------------------------------------------------------------------- radio */

export function RadioGroup({
  value,
  defaultValue,
  onChange,
  row,
  children,
  className = ''
}: {
  value?: string
  defaultValue?: string
  onChange: (value: string) => void
  row?: boolean
  children: ReactNode
  className?: string
}) {
  return (
    <BaseRadioGroup
      value={value}
      defaultValue={defaultValue}
      onValueChange={next => onChange(String(next))}
      className={`${row ? 'ui-radio-group--row' : ''} ${className}`}
    >
      {children}
    </BaseRadioGroup>
  )
}

export function Checkbox({
  checked,
  defaultChecked,
  onChange,
  label,
  name
}: {
  checked?: boolean
  defaultChecked?: boolean
  onChange?: (checked: boolean) => void
  label?: ReactNode
  name?: string
}) {
  const control = (
    <BaseCheckbox.Root
      name={name}
      checked={checked}
      defaultChecked={defaultChecked}
      onCheckedChange={next => onChange?.(Boolean(next))}
      className="ui-checkbox"
    >
      <BaseCheckbox.Indicator className="ui-checkbox__indicator" keepMounted>
        <svg viewBox="0 0 12 12" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square">
          <path d="M2 6.5 5 9.5 10 3" />
        </svg>
      </BaseCheckbox.Indicator>
    </BaseCheckbox.Root>
  )

  if (label === undefined) return control

  return (
    <label className="ui-checkbox-label">
      {control}
      {label}
    </label>
  )
}

/* -------------------------------------------------------------------- tabs */

export interface TabOption<T> {
  value: T
  label: ReactNode
}

// The tab bar only; the caller renders the active panel. Base UI provides
// the tablist roles and roving keyboard focus.
export function Tabs<T extends string>({
  value,
  onChange,
  tabs,
  className = ''
}: {
  value: T
  onChange: (value: T) => void
  tabs: TabOption<T>[]
  className?: string
}) {
  return (
    <BaseTabs.Root value={value} onValueChange={next => onChange(next as T)}>
      <BaseTabs.List className={`ui-tabs ${className}`}>
        {tabs.map(tab => (
          <BaseTabs.Tab key={tab.value} value={tab.value} className="ui-tab">
            {tab.label}
          </BaseTabs.Tab>
        ))}
        <BaseTabs.Indicator className="ui-tabs__indicator" />
      </BaseTabs.List>
    </BaseTabs.Root>
  )
}

export function Radio({ value, label }: { value?: string; label?: ReactNode }) {
  const control = (
    <BaseRadio.Root value={value} className="ui-radio">
      <BaseRadio.Indicator className="ui-radio__indicator" keepMounted />
    </BaseRadio.Root>
  )

  if (label === undefined) return control

  return (
    <label className="ui-radio-label">
      {control}
      {label}
    </label>
  )
}
